import { useState } from "react";
import { Check, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ClarifyQuestion {
  question: string;
  options: string[];
}

interface ClarifyData {
  original_text: string;
  questions: ClarifyQuestion[];
  // Backend flips this to true once the user submits answers, so the
  // widget can render a read-only Q&A summary on refresh instead of
  // disappearing or letting the user answer the same thing twice.
  answered?: boolean;
  answers?: Record<string, string>;
  // Mid-series progress — persisted on every Next/Skip tap so a
  // refresh rehydrates to the exact question the user was on with
  // their earlier answers intact.
  current_idx?: number;
  partial_answers?: Record<string, string>;
}

interface ClarifyWidgetProps {
  data: ClarifyData;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

/**
 * Multi-step clarifying questions widget.
 *
 * Shows one question at a time with multiple-choice options + free text.
 * Answers are collected and sent back to the backend to build an enhanced prompt.
 *
 * - Skip: advances without recording an answer
 * - Close (x): dismisses widget, proceeds with original prompt unchanged
 * - After last question: auto-submits all answers
 */
export function ClarifyWidget({ data, onAction }: ClarifyWidgetProps) {
  // Rehydrate mid-series progress from persisted state when present.
  // Lazy initializers run once on mount so a prop change later (e.g.
  // the same widget_json arriving again from a replay) won't clobber
  // the user's in-flight selection.
  const [currentIdx, setCurrentIdx] = useState(() => {
    const persisted = data.current_idx ?? 0;
    const last = Math.max(0, (data.questions?.length ?? 1) - 1);
    return Math.min(Math.max(0, persisted), last);
  });
  const [answers, setAnswers] = useState<Record<number, string>>(() => {
    // Convert string-keyed partial_answers from the backend into the
    // number-keyed map this component uses internally.
    const out: Record<number, string> = {};
    const partial = data.partial_answers ?? {};
    for (const [k, v] of Object.entries(partial)) {
      const idx = Number(k);
      if (!Number.isNaN(idx) && v) out[idx] = v;
    }
    return out;
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [customText, setCustomText] = useState("");

  const questions = data.questions ?? [];
  const total = questions.length;

  // Read-only summary when the widget has been answered. ChatShell
  // stamps widgetData.answered=true the moment the user submits, so
  // the transition happens in place — no chat bubble echo, no gap
  // while the AI is thinking. History replay after refresh uses the
  // same path via widget_json.data persisted on the backend.
  if (data.answered) {
    const answered = data.answers ?? {};
    const entries = questions
      .map((q, i) => ({ question: q.question, answer: answered[String(i)] }))
      .filter((e) => e.answer && e.answer.trim());

    if (entries.length === 0) {
      return (
        <Card
          variant="default"
          padding="default"
          className="max-w-md opacity-60"
        >
          <p className="text-xs text-daydra-muted">
            Clarification dismissed.
          </p>
        </Card>
      );
    }

    return (
      <Card
        variant="default"
        padding="default"
        className="max-w-md opacity-80"
      >
        <div className="flex items-center gap-2 mb-3">
          <Check size={14} className="text-daydra-success" />
          <p className="text-xs text-daydra-muted">
            Answered
          </p>
        </div>
        <div className="space-y-3">
          {entries.map((entry, i) => (
            <div key={i}>
              <p className="text-xs text-daydra-muted mb-1">{entry.question}</p>
              <p className="text-sm text-daydra-text">{entry.answer}</p>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  const current = questions[currentIdx];
  if (!current || total === 0) return null;

  const isLast = currentIdx >= total - 1;

  const submit = (finalAnswers: Record<number, string>) => {
    // ChatShell's onAction handler stamps widgetData.answered=true
    // when it sees a "clarify_response" action, which flips this
    // component to its read-only summary on the very next render.
    // Include the question texts so the backend can persist labels
    // onto widget_json for future history replays.
    const questionTexts: Record<number, string> = {};
    questions.forEach((q, i) => {
      questionTexts[i] = q.question;
    });
    onAction?.("clarify_response", {
      __action: "clarify_response",
      // __busy triggers the thinking indicator without removing this
      // widget from the chat — the summary card must stay visible.
      __busy: true,
      __payload: {
        original_text: data.original_text,
        answers: finalAnswers,
        questions: questionTexts,
      },
    });
  };

  // Fire-and-forget progress ping. Writes to widget_json.data so a
  // refresh rehydrates the exact question + collected answers.
  const saveProgress = (nextIdx: number, nextAnswers: Record<number, string>) => {
    onAction?.("clarify_progress", {
      __action: "clarify_progress",
      __silent: true,
      __payload: {
        current_idx: nextIdx,
        answers: nextAnswers,
      },
    });
  };

  const advance = () => {
    // Record the answer
    const answer = customText.trim() || selected;
    const updated = { ...answers };
    if (answer) {
      updated[currentIdx] = answer;
    }
    setAnswers(updated);

    // Reset state for next question
    setSelected(null);
    setCustomText("");

    if (isLast) {
      submit(updated);
    } else {
      const nextIdx = currentIdx + 1;
      setCurrentIdx(nextIdx);
      saveProgress(nextIdx, updated);
    }
  };

  const skip = () => {
    setSelected(null);
    setCustomText("");

    if (isLast) {
      submit(answers);
    } else {
      const nextIdx = currentIdx + 1;
      setCurrentIdx(nextIdx);
      saveProgress(nextIdx, answers);
    }
  };

  const close = () => {
    // Dismiss and proceed with original prompt unchanged
    submit({});
  };

  const hasAnswer = !!(customText.trim() || selected);

  return (
    <Card variant="default" padding="default" className="max-w-md animate-scale-in relative">
      {/* Close button */}
      <button
        onClick={close}
        className="absolute top-3 right-3 p-1 rounded-lg text-daydra-muted hover:text-daydra-text hover:bg-daydra-raised transition-colors"
        aria-label="Close"
      >
        <X size={14} />
      </button>

      {/* Question counter */}
      {total > 1 && (
        <p className="text-2xs text-daydra-muted mb-2">
          {currentIdx + 1} of {total}
        </p>
      )}

      {/* Question */}
      <p className="text-sm text-daydra-text font-medium mb-3 pr-6">
        {current.question}
      </p>

      {/* Options */}
      <div className="flex flex-col gap-1.5 mb-3">
        {current.options.map((option) => (
          <button
            key={option}
            onClick={() => {
              setSelected(option);
              setCustomText("");
            }}
            className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
              selected === option
                ? "border-daydra-accent/50 bg-daydra-accent-ghost text-daydra-text"
                : "border-transparent hover:bg-daydra-raised text-daydra-dim hover:text-daydra-text"
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      {/* Free text input */}
      <Input
        placeholder="Or type your own..."
        value={customText}
        onChange={(e) => {
          setCustomText(e.target.value);
          if (e.target.value.trim()) setSelected(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && hasAnswer) advance();
        }}
        className="h-9 text-sm mb-3"
      />

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={skip}>
          Skip
        </Button>
        <Button size="sm" onClick={advance} disabled={!hasAnswer}>
          {isLast ? "Done" : "Next"}
        </Button>
      </div>
    </Card>
  );
}
